import type {
  EnvelopeRecipient,
  InteractionDocument,
  InteractionEnvelope,
} from '@/common/types/interaction';
import type { GQLInteractionType } from '@/generated/graph-api';
import type { OfferEnvelope } from '../../components/GenerateOfferModal/types';
import type { NormalizedEnvelopeStatus, SentEnvelope } from './types';

/** Collapse `delivered` status to `sent`. */
export const normalizeEnvelopeStatus = (
  status: InteractionEnvelope['status'],
): NormalizedEnvelopeStatus => (status === 'delivered' ? 'sent' : status);

/** Recipients that still need to sign (not completed, of type signer). */
export const getUnsignedRecipients = <
  T extends Pick<EnvelopeRecipient, 'status' | 'type' | 'name'>,
>(
  recipients: readonly T[],
): T[] =>
  recipients.filter(
    (recipient) =>
      !!recipient &&
      recipient.status !== 'completed' &&
      recipient.type === 'Signer',
  );

/** Formats unsigned signer names, e.g. "Jane, John, and Jack"; null when all signed. */
export const getFormattedUnsignedRecipients = (
  recipients: readonly Pick<EnvelopeRecipient, 'status' | 'type' | 'name'>[],
): string | null => {
  const names = getUnsignedRecipients(recipients).map(
    (recipient) => recipient.name,
  );

  if (names.length === 0) {
    return null;
  }

  const replacement = names.length > 2 ? ', and$1' : ' and$1';
  return names.join(', ').replace(/,([^,]*)$/, replacement);
};

const documentsForEnvelope = (
  envelopeId: string,
  documents: readonly InteractionDocument[],
): InteractionDocument[] =>
  documents.filter((document) => document.interactionEnvelopeId === envelopeId);

/** Reshapes a core-service envelope and documents into the `SentEnvelope` shape. */
export const buildSentEnvelope = (
  envelope: InteractionEnvelope,
  documents: readonly InteractionDocument[],
  interactionId: string,
  interactionType: GQLInteractionType,
): SentEnvelope => ({
  ...envelope,
  status: normalizeEnvelopeStatus(envelope.status),
  interactionId,
  interactionType,
  listOfUnsignedRecipients: getFormattedUnsignedRecipients(envelope.recipients),
  documents: documentsForEnvelope(envelope.id, documents),
});

/** Reshapes a core-service envelope and documents into the `OfferEnvelope` shape. */
export const buildOfferEnvelope = (
  envelope: InteractionEnvelope,
  documents: readonly InteractionDocument[],
): OfferEnvelope => ({
  id: envelope.id,
  docusignEnvelopeId: envelope.docusignEnvelopeId,
  name: envelope.name,
  createdAt: envelope.createdAt,
  status: normalizeEnvelopeStatus(envelope.status),
  documents: documentsForEnvelope(envelope.id, documents),
});
